import { useState, useEffect, useRef } from "react";
import { useUser } from "../contextapi/UserContext";
import { offlineSupabase } from "../utils/offlineSupabaseProxy";

interface AdminAuthState {
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAdminAuth = () => {
  const { user } = useUser();
  const [adminState, setAdminState] = useState<AdminAuthState>({
    isAdmin: false,
    isLoading: true,
    error: null,
  });
  
  // Cache to prevent re-checking for the same user
  const checkedUserIdRef = useRef<string | null>(null);
  const cachedAdminStatusRef = useRef<boolean | null>(null);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        checkedUserIdRef.current = null;
        cachedAdminStatusRef.current = null;
        setAdminState({
          isAdmin: false,
          isLoading: false,
          error: null,
        });
        return;
      }

      // If we've already checked this user ID, use cached result
      if (checkedUserIdRef.current === user.id && cachedAdminStatusRef.current !== null) {
        setAdminState({
          isAdmin: cachedAdminStatusRef.current,
          isLoading: false,
          error: null,
        });
        return;
      }

      try {
        setAdminState((prev) => ({ ...prev, isLoading: true, error: null }));

        const { data: userRole, error } = await offlineSupabase
          .rpc("get_user_role", { user_id: user.id });

        if (error) {
          throw error;
        }

        const isAdmin = userRole === "admin";
        
        // Cache the result
        checkedUserIdRef.current = user.id;
        cachedAdminStatusRef.current = isAdmin;

        setAdminState({
          isAdmin,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error("Error checking admin role:", error);
        setAdminState({
          isAdmin: false,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to check admin role",
        });
      }
    };

    checkAdminRole();
  }, [user?.id]); // Only depend on user.id, not the entire user object

  return adminState;
};
